import { CONNECTION_KIND } from '../constants';

/**
 * Finds inventory source for a given kind from shop data
 * Handles both new (inventorySources array) and legacy formats
 *
 * @param {Object} shop - Shop object
 * @param {'stock'|'product'} kind - Connection kind
 * @returns {Object|null} Inventory source or null
 */
export const findInventorySource = (shop, kind) => {
	if (!shop) return null;

	// Try new format first
	if (shop.inventorySources && shop.inventorySources.length > 0) {
		return shop.inventorySources.find((source) => source.kind === kind) || null;
	}

	// Fallback to legacy format
	return kind === CONNECTION_KIND.STOCK ? shop.stockInventorySource || null : shop.productInventorySource || null;
};

/**
 * Whether the POS-connection onboarding step is genuinely complete (NEA-5988).
 *
 * The bare `!!pointOfSale` check this replaces read the eager pre-OAuth
 * placeholder (created with empty credentials before the browser ever leaves
 * for the POS) as complete, which unmounted the step — and the OAuth code
 * exchange it hosts — on the return leg.
 *
 * - `hasCredentials` is required for oauth providers ONLY: ~20 plug-play
 *   providers (woocommerce, ftp, email, sap, shopify, …) authenticate via
 *   upload key / plugin and never write credentials to the source row.
 * - `requiresLocationMapping` (oauth + locationsUrl providers, flagged
 *   server-side by get-my-shops) additionally requires a chosen outlet —
 *   an unmapped multi-outlet source would ingest every outlet merged.
 *
 * @param {Object|null} source - Formatted stock inventory source from /api/my-shop
 * @returns {boolean}
 */
export const isPosConnectionComplete = (source) => {
	if (!source?.pointOfSale) return false;
	if (source.connectType === 'oauth' && !source.hasCredentials) return false;
	if (source.requiresLocationMapping && !source.locationId) return false;
	return true;
};

/**
 * Iterates through all fields in provider configuration
 *
 * @param {Object} provider - Provider configuration
 * @param {Function} callback - Called with (field, step) for each field
 */
export const iterateProviderFields = (provider, callback) => {
	if (!provider?.connect?.steps) return;

	for (const step of provider.connect.steps) {
		if (step.fields) {
			for (const field of step.fields) {
				callback(field, step);
			}
		}
	}
};

/**
 * Extracts session ID from API response
 * Tries response.sessionId first, then parses from finishSetupLink
 *
 * @param {Object} response - API response from startInventoryConnection
 * @returns {string|null} Session ID or null
 */
export const extractSessionId = (response) => {
	if (!response) return null;

	// Try direct sessionId first
	if (response.sessionId) {
		return response.sessionId;
	}

	// Parse from finishSetupLink URL
	if (response.finishSetupLink) {
		try {
			const parts = response.finishSetupLink.split('/');
			const sessionIdValue = parts.includes('initiate-oauth') ? parts[parts.length - 2] : parts[parts.length - 1];
			return sessionIdValue;
		} catch (error) {
			console.error('Failed to parse session ID from URL:', error);
		}
	}

	return null;
};

/**
 * Transforms form fields for per-location providers
 * Wraps perLocation field values in shop ID object
 *
 * @param {Object} formData - Form data object
 * @param {Object} provider - Provider configuration
 * @param {string} shopId - Current shop ID
 * @returns {Object} Transformed form data
 */
export const transformFormFields = (formData, provider, shopId) => {
	const transformed = { ...formData };

	iterateProviderFields(provider, (field) => {
		if (field.perLocation && field.saveAs && transformed[field.saveAs]) {
			transformed[field.saveAs] = {
				[shopId]: transformed[field.saveAs]
			};
		}
	});

	return transformed;
};

/**
 * Gets the display connection data based on edit mode
 * Returns the appropriate stock or product connection data
 *
 * @param {boolean} isEditingProduct - Whether editing product connection
 * @param {Object} stock - Stock connection data
 * @param {Object} product - Product connection data
 * @returns {Object} Display connection data
 */
export const getDisplayConnection = (isEditingProduct, stock, product) => {
	return {
		pos: isEditingProduct ? product.selectedPOS : stock.selectedPOS,
		provider: isEditingProduct ? product.provider : stock.provider,
		formData: isEditingProduct ? product.formData : stock.formData,
		formErrors: isEditingProduct ? product.formErrors : stock.formErrors,
		oauthParams: isEditingProduct ? product.oauthParams : stock.oauthParams,
		oauthParamsChange: isEditingProduct ? product.setOauthParams : stock.setOauthParams,
		sessionId: isEditingProduct ? product.sessionId : stock.sessionId,
		formChange: isEditingProduct ? product.onFormChange : stock.onFormChange,
		posChange: isEditingProduct ? product.onPOSChange : stock.onPOSChange,
		isPolling: isEditingProduct ? product.pollingForConnection : stock.pollingForConnection
	};
};

/**
 * Filters the POS list based on the current edit mode
 *
 * @param {Array} posList - List of available POS providers
 * @param {boolean} isEditingProduct - Whether editing product connection
 * @param {boolean} isInitialSetup - Whether in initial setup mode
 * @returns {Array} Filtered POS list
 */
export const getFilteredPosList = (posList, isEditingProduct, isInitialSetup) => {
	if (isEditingProduct) {
		return posList.filter((pos) => pos.kinds?.includes(CONNECTION_KIND.PRODUCT));
	}

	if (isInitialSetup) {
		return posList.filter((pos) => pos.kinds?.includes(CONNECTION_KIND.STOCK));
	}

	return posList;
};

/**
 * Determines if loading state should be shown
 *
 * @param {boolean} businessDetailsCompleted - Whether business details are completed
 * @param {boolean} editingStock - Whether editing stock connection
 * @param {boolean} editingProduct - Whether editing product connection
 * @param {boolean} loadingInventorySources - Whether loading inventory sources
 * @param {boolean} isCompleted - Whether POS connection is completed
 * @param {string} stockSelectedPOS - Selected stock POS
 * @returns {boolean} Whether to show loading state
 */
export const shouldShowLoading = (
	businessDetailsCompleted,
	editingStock,
	editingProduct,
	loadingInventorySources,
	isCompleted,
	stockSelectedPOS
) => {
	return (
		businessDetailsCompleted &&
		!editingStock &&
		!editingProduct &&
		(loadingInventorySources || !(isCompleted === false || (isCompleted && stockSelectedPOS)))
	);
};

/**
 * Resets connection edit state
 *
 * @param {Object} stock - Stock connection object
 * @param {Object} product - Product connection object
 */
export const resetEditState = (stock, product) => {
	stock.setEditing(false);
	product.setEditing(false);
	stock.setEditingPOS('');
	product.setEditingPOS('');
};
